import React, { useMemo, useRef } from 'react';
import { diff } from 'deep-object-diff';
import useTeams from '@hooks/useTeams';
import MaterialTable from '@components/molecules/MaterialTable';
import { Column, Options } from '@material-table/core';
import { STATUS, TeamAthlete } from '@type/db-types';
import { toast } from 'react-toastify';
import { TableActionData } from '@type/mui-types';
import { updateUserAndMembership } from '@modules/supabase/users';
import invariant from 'tiny-invariant';
import { stringFromError } from '@utils/utils';
import { log } from '@utils/logging';
import { Grid2 as Grid, Typography } from '@mui/material';
import { updateTeamStatus } from '@modules/supabase/teams';
import useUser from '@hooks/useUser';
import { editableUserColumns } from '@utils/user-table-utils';

export default function Parents({ editable }: { editable: boolean }) {
    const currentUser = useUser();
    const tableRef: React.MutableRefObject<undefined | unknown> = useRef();
    const { parents, refreshMembers, selectedTeam } = useTeams();

    const tableColumns = useMemo(
        () => editableUserColumns({ userKey: 'id' }) as unknown as Column<TeamAthlete>[],
        [],
    );

    const onSuccess = () => {
        // takes a minute for the updated info to trickle through
        // TODO: eventually we should hook this up to a watcher...
        toast.info('Updating parents...', { autoClose: 1000 });
        setTimeout(() => refreshMembers(), 1000);
    };

    const onRowUpdate = async (newData: TeamAthlete, oldData?: TeamAthlete | undefined) => {
        const updatedFields: Partial<TeamAthlete> & TableActionData = diff(oldData || {}, newData);
        if ('tableData' in updatedFields) delete updatedFields.tableData;

        if (Object.keys(updatedFields).length) {
            invariant(selectedTeam, 'Team must be selected to add a parent');

            const user: Partial<Omit<TeamAthlete, 'weight'>> = {
                ...oldData,
                ...updatedFields,
            };
            await updateUserAndMembership(user).catch((e) =>
                log(`[Parents:edit] ${stringFromError(e)}`),
            );
            onSuccess();
        } else {
            toast.info('No changes detected');
            return Promise.resolve();
        }
    };

    // if no team selected, show a message
    if (!selectedTeam) {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                style={{ height: '90vh', width: '100%' }}
            >
                <Grid>
                    <Typography variant="h5">Please select a team to view parents</Typography>
                </Grid>
            </Grid>
        );
    }

    const onRowDelete = async (oldData: TeamAthlete) => {
        if (currentUser.user?.id === oldData.userId) {
            return toast.error('You cannot remove yourself');
        }
        await toast
            .promise(updateTeamStatus(STATUS.REMOVED, oldData.membershipId), {
                pending: 'Removing parent...',
                success: 'Parent removed',
                error: 'Error removing parent',
            })
            .then(refreshMembers);
    };

    return (
        <div>
            <MaterialTable<TeamAthlete>
                tableRef={tableRef}
                // actions={actions}
                columns={tableColumns}
                data={parents || []}
                editable={{
                    isEditable: () => editable,
                    onRowUpdate,
                    onRowDelete,
                }}
                localization={{
                    toolbar: {
                        nRowsSelected: (number) =>
                            `${number} parent${number === 1 ? '' : 's'} selected`,
                    },
                }}
                loading={parents === undefined}
                options={TABLE_OPTIONS}
                style={{ boxShadow: 'none' }}
                tableId="BrandBadges"
                title="Parents"
            />
        </div>
    );
}

const TABLE_OPTIONS: Options<TeamAthlete> = {
    actionsColumnIndex: -1,
    // maxBodyHeight: `${materialTableHeight + 3}vh`,
    // minBodyHeight: `${materialTableHeight + 3}vh`,
};
